import { fetchWithTimeout } from './fetchWithTimeout';
import { MenuConfig } from './menu-config';
import { PageConfig } from './page-config';
import { User } from './user';

export type UserGroup = {
    userGroupId?: string;
    id?: string;
    descr?: string;
    authority?: number;
    createBy?: string;
    createDate?: Date;
    updateBy?: string;
    updateDate?: Date;
    users?: User[];
    menuConfigs?: MenuConfig[];
    pageConfigs?: PageConfig[];
};

export const getMaxAuthority = (userGroups?: UserGroup[]) => {
    const authorities = userGroups?.map((userGroup) => userGroup.authority || 0) || [0];
    const maxAuthority = Math.max(...authorities);
    return maxAuthority;
};

type CreateRequestData = UserGroup & {};

type CreateResponseData = {
    statusCode: number;
    message: string;
    success: boolean;
    data?: UserGroup;
};

function create(data: CreateRequestData) {
    return fetchWithTimeout('user-group/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    })
        .then((res) => res.json())
        .then((data: CreateResponseData) => data)
        .catch((error) => {
            throw error;
        })
        .finally(() => {});
}

type CreateBulkResponseData = {
    statusCode: number;
    message: string;
    success: boolean;
    data?: UserGroup[];
};

function createBulk(data: CreateRequestData[]) {
    return fetchWithTimeout('user-group/createBulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    })
        .then((res) => res.json())
        .then((data: CreateBulkResponseData) => data)
        .catch((error) => {
            throw error;
        })
        .finally(() => {});
}

type GetRequestData = UserGroup & {
    select?: (keyof UserGroup)[];
    relations?: {
        [key in keyof Pick<UserGroup, 'users' | 'menuConfigs' | 'pageConfigs'>]?: boolean;
    };
};

type GetResponseData = {
    statusCode: number;
    message: string;
    success: boolean;
    data?: UserGroup[];
};

function get(data: GetRequestData) {
    return fetchWithTimeout('user-group/get', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    })
        .then((res) => res.json())
        .then((data: GetResponseData) => data)
        .catch((error) => {
            throw error;
        })
        .finally(() => {});
}

type UpdateRequestData = UserGroup & {
    userGroupId: string;
};

type UpdateResponseData = {
    statusCode: number;
    message: string;
    success: boolean;
    data?: UserGroup;
};

function update(data: UpdateRequestData) {
    return fetchWithTimeout('user-group/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    })
        .then((res) => res.json())
        .then((data: UpdateResponseData) => data)
        .catch((error) => {
            throw error;
        })
        .finally(() => {});
}

type UpdateBulkResponseData = {
    statusCode: number;
    message: string;
    success: boolean;
    data?: UserGroup[];
};

function updateBulk(data: UpdateRequestData[]) {
    return fetchWithTimeout('user-group/updateBulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    })
        .then((res) => res.json())
        .then((data: UpdateBulkResponseData) => data)
        .catch((error) => {
            throw error;
        })
        .finally(() => {});
}

type RemoveRequestData = UserGroup & { userGroupId: string };

type RemoveResponseData = {
    statusCode: number;
    message: string;
    success: boolean;
    data?: {};
};

function remove(data: RemoveRequestData) {
    return fetchWithTimeout('user-group/remove', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    })
        .then((res) => res.json())
        .then((data: RemoveResponseData) => data)
        .catch((error) => {
            throw error;
        })
        .finally(() => {});
}

function removeBulk(data: RemoveRequestData[]) {
    return fetchWithTimeout('user-group/removeBulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    })
        .then((res) => res.json())
        .then((data: RemoveResponseData) => data)
        .catch((error) => {
            throw error;
        })
        .finally(() => {});
}

export default { create, createBulk, get, update, updateBulk, remove, removeBulk };
